// Copyright (C) 2021 Bystronic Laser AG

import { EChartsOption } from 'echarts';
import { FilterTimeAxisSpanEnum } from '../enums/FilterTimeAxisSpanEnum';
import { FilterTimeSpanEnum } from '../enums/FilterTimeSpanEnum';
import { ChartGenerator, ProcedureName } from './abstract/chartGenerator';
import { GeneratorParams } from './generatorParams';
import { BendTrendChronologicalProductivityTimelineData } from './chartsData';
import { getTooltipTitle } from '@/utils/charts';
import { abbreviateNumber, abbreviateNumberWithUnit } from '@/utils/number';
import i18n from '@/i18n';
import { metricsService } from '@/services/metrics.service';
import { responseHandler } from '@/services/response.handler';

export default class BendTrendChronologicalProductivityTimelineGenerator extends ChartGenerator<
  BendTrendChronologicalProductivityTimelineData[]
> {
  constructor(procedure: ProcedureName, private readonly customerIdDh: number) {
    super(procedure);
  }

  override async getData(
    selectedDevices: string[],
    selectedShifts: number[],
    timeSpan: FilterTimeSpanEnum | [string, string],
    timeAxisSpan: FilterTimeAxisSpanEnum,
    params?: { [key: string]: any },
    customerNo?: string,
  ): Promise<BendTrendChronologicalProductivityTimelineData[]> {
    const dateRange = timeSpan as [string, string];
    return metricsService
      .getBendingDevicesMetrics<
        BendTrendChronologicalProductivityTimelineData[]
      >(
        this.procedure,
        {
          customerIdDh: this.customerIdDh,
          startDate: dateRange[0],
          endDate: dateRange[1],
          dateGrouping: timeAxisSpan,
          shiftIds: selectedShifts,
          deviceStringIds: selectedDevices,
        },
        this.controller,
      )
      .then((response) => responseHandler.handleDates(response));
  }

  override updateOptions(
    data: BendTrendChronologicalProductivityTimelineData[],
    parameters: GeneratorParams,
    prevOptions?: EChartsOption,
  ): EChartsOption {
    return {
      legend: {
        bottom: 0,
      },
      xAxis: {
        type: 'time',
        axisPointer: {
          type: 'shadow',
        },
      },
      yAxis: [
        {
          type: 'value',
          name: '%',
        },
        {
          type: 'value',
          name: 'h',
          axisLabel: {
            formatter: (value: number) => abbreviateNumber(value),
          },
        },
      ],
      grid: {
        containLabel: true,
        left: 0,
        right: 10,
        top: 10,
        bottom: 30,
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const [xAxisValue] = params[0].value;
          const seriesInfo = params
            .map((x: any) => {
              const unit = x.seriesType === 'line' ? '%' : 'h';
              return `<p>${x.marker}<span style="font-weight: bold">${
                x.seriesName
              }</span>: ${abbreviateNumberWithUnit(x.value[1], unit, 2)}</p>`;
            })
            .join('');
          return `
          <p>${getTooltipTitle(xAxisValue, parameters.timeAxisSpan!, false)}</p>
          ${seriesInfo}
          `;
        },
        confine: false,
      },
      series: [
        {
          name: i18n.t('bend_productivity_widget.title').toString(),
          type: 'line',
          data: data.map((x) => [x.timestamp, x.productivityPercentage]),
        },
        {
          name: i18n.t('bend_productivity_widget.bending').toString(),
          type: 'bar',
          stack: 'stack',
          data: data.map((x) => [x.timestamp, x.bendingTimeSeconds / 3600]),
          itemStyle: {
            color: '#24ae61',
          },
          yAxisIndex: 1,
        },
        {
          name: i18n.t('bend_productivity_widget.stand-by').toString(),
          type: 'bar',
          stack: 'stack',
          data: data.map((x) => [x.timestamp, x.standbyTimeSeconds / 3600]),
          itemStyle: {
            color: '#a2aeaa',
          },
          yAxisIndex: 1,
        },
      ],
    };
  }
}
